import Snackbar from '@mui/material/Snackbar';
import { Box, Tabs, Tab } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import WeekHeader from './components/WeekHeader';
import { useCopyDay } from './hook/useCopyDay';
import { usePlanningData } from './hook/usePlanningData';
import PlanningFooter from './PlanningFooter';
import PlanningSensorList from './PlanningSensorList';
import S from './PlanningVue.module.scss';
import { getUnavailableDatesPeriodes, parseYMD } from './PlanningVue.utils';
import PlanningVueControls from './PlanningVueControls';
import PlanningVueLoader from './PlanningVueLoader';
import PlanningVueTabs from './PlanningVueTabs';
import { Mode } from './types';

const Planning = (props: any) => {
  const { viewId: viewIdFromUrl } = useParams<{ viewId?: string }>();
  const resolvedViewId =
    viewIdFromUrl ?? (props.viewId ? String(props.viewId) : undefined);

  // Hook personnalisé pour la logique métier
  const {
    defaultModes,
    selectedMode,
    setSelectedMode,
    markerMatrix,
    setMarkerMatrix,
    timelineMatrix,
    setTimelineMatrix,
    tasks,
    setTasks,
    defaultTask,
    actualView,
    loading,
    savingState,
    setSavingState,
    fetchTasks,
    handleSave: executeSave,
  } = usePlanningData({ viewId: resolvedViewId, projectId: props.projectId });

  // État local du composant
  const [currentInteractionMode, setCurrentInteractionMode] =
    useState<string>('color');
  const [exMarkerMatrix, setExMarkerMatrix] = useState<number[][]>();
  const [exTimelineMatrix, setExTimelineMatrix] = useState<Mode[][]>();
  const [exceptionPlanningInfo, setExceptionPlanningInfo] = useState<{
    title: string;
    startDate: Date;
    endDate: Date;
  }>({ title: '', startDate: new Date(), endDate: new Date() });
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const isBusy = loading || savingState === 1;

  // Hook de copie jour
  const {
    sourceDayForCopy,
    targetDaysForCopy,
    handleDaySelection,
    resetCopyMode,
  } = useCopyDay(
    exTimelineMatrix ? exTimelineMatrix : timelineMatrix,
    exMarkerMatrix ? exMarkerMatrix : markerMatrix
  );

  // Initialisation
  useEffect(() => {
    setSavingState(2);
    fetchTasks();
  }, []);

  // Callbacks
  const handleTabNav = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0 || newValue === 1) {
      setExceptionPlanningInfo({
        title: '',
        startDate: new Date(),
        endDate: new Date(),
      });
      setExMarkerMatrix(undefined);
      setExTimelineMatrix(undefined);
    }
    setTabValue(newValue);
  };

  const toggleMarkerMode = useCallback(() => {
    setCurrentInteractionMode('add-marker');
  }, []);

  const toggleRemoveMode = useCallback(() => {
    setCurrentInteractionMode('remove-marker');
  }, []);

  const toggleCopyMode = useCallback(() => {
    if (currentInteractionMode === 'copy-day') {
      setCurrentInteractionMode('color');
      resetCopyMode();
    } else {
      setCurrentInteractionMode('copy-day');
      resetCopyMode();
    }
  }, [currentInteractionMode, resetCopyMode]);

  const handleMarkerMatrix = (dayIndex: number, newMarkers: number[]) => {
    const newMatrix = exceptionPlanningInfo.title
      ? [...exMarkerMatrix!]
      : [...markerMatrix];
    const markersWithZero = newMarkers.includes(0)
      ? newMarkers
      : [0, ...newMarkers];
    newMatrix[dayIndex] = markersWithZero;
    exceptionPlanningInfo.title
      ? setExMarkerMatrix(newMatrix)
      : setMarkerMatrix(newMatrix);
    setSavingState(0);
  };

  const handleTimelineMatrix = (dayIndex: number, newTimeline: Mode[]) => {
    const newMatrix = exceptionPlanningInfo.title
      ? [...exTimelineMatrix!]
      : [...timelineMatrix];
    newMatrix[dayIndex] = newTimeline;
    exceptionPlanningInfo.title
      ? setExTimelineMatrix(newMatrix)
      : setTimelineMatrix(newMatrix);
    setSavingState(0);
  };

  const displayExPlanning = (e: any) => {
    const allDaysInbetween = Array.from(
      {
        length:
          (parseYMD(e.cron_end_date, '-').getTime() -
            parseYMD(e.cron_start_day, '-').getTime()) /
            (1000 * 60 * 60 * 24) +
          1,
      },
      (_, i) => {
        const d = new Date(parseYMD(e.cron_start_day, '-'));
        d.setDate(d.getDate() + i);
        return d;
      }
    );

    const markerArray = Array.from({ length: allDaysInbetween.length }, () => [
      0,
    ]);
    const timelineArray = Array.from({ length: allDaysInbetween.length }, () =>
      Array.from({ length: 48 }, () => defaultModes[0])
    );
    const dayModes: { [key: string]: { [key: number]: Mode } } = {};

    const exceptionTasks = tasks.filter(
      (task: any) =>
        task.state === 3 &&
        task.cron_start_day === e.cron_start_day &&
        task.cron_end_date === e.cron_end_date
    );

    exceptionTasks.forEach((task: any) => {
      const dayIndex = allDaysInbetween.findIndex(
        (date) =>
          date.getTime() ===
          new Date(task.cron_year, task.cron_month - 1, task.cron_day).getTime()
      );
      const hour = parseInt(task.cron_hour!, 10);
      const minute = parseInt(task.cron_minute!, 10);
      const markerValue = hour * 2 + (minute === 30 ? 1 : 0);

      if (!dayModes[dayIndex]) {
        dayModes[dayIndex] = {};
      }
      const mode =
        defaultModes.find(
          (mode: any) =>
            mode.id ===
            ((task.id_arguments && task.id_arguments[0]?.Value) || '')
        ) || defaultModes[0];
      dayModes[dayIndex][markerValue] = mode;
      if (!markerArray[dayIndex]?.includes(markerValue)) {
        markerArray[dayIndex].push(markerValue);
      }
    });

    Object.entries(dayModes).forEach(([dayIndex, markers]) => {
      const dayIndexNum = parseInt(dayIndex);
      const sortedMarkers = Object.keys(markers)
        .map(Number)
        .sort((a, b) => a - b);
      for (let i = 0; i < 48; i++) {
        const lastMarker = sortedMarkers.filter((marker) => marker <= i).pop();
        if (lastMarker !== undefined) {
          timelineArray[dayIndexNum][i] = markers[lastMarker];
        }
      }
    });

    setExPlanningInfo({
      title: e.name,
      startDate: parseYMD(e.cron_start_day, '-'),
      endDate: parseYMD(e.cron_end_date, '-'),
    });
    setExMarkerMatrix(markerArray);
    setExTimelineMatrix(timelineArray);
    setTabValue(3);
  };

  const removeExceptionPlanning = (e: any) => {
    setTasks(
      tasks.filter(
        (task) =>
          e.cron_start_day !== task.cron_start_day &&
          e.cron_end_date !== task.cron_end_date
      )
    );
    setSavingState(0);
  };

  const setExPlanningInfo = (data: {
    title: string;
    startDate: Date;
    endDate: Date;
  }) => {
    setExceptionPlanningInfo(data);

    // Initialize matrices for the exception planning period
    const numberOfDays =
      Math.ceil(
        (data.endDate.getTime() - data.startDate.getTime()) /
          (1000 * 60 * 60 * 24)
      ) + 1;

    const initialMarkerMatrix = Array.from({ length: numberOfDays }, () => [0]);
    const initialTimelineMatrix = Array.from({ length: numberOfDays }, () =>
      Array.from({ length: 48 }, () => defaultModes[0])
    );

    setExMarkerMatrix(initialMarkerMatrix);
    setExTimelineMatrix(initialTimelineMatrix);
    setTabValue(3);
  };

  const onDaySelected = useCallback(
    (dayIndex: number) => {
      const result = handleDaySelection(dayIndex, currentInteractionMode);
      if (result) {
        const { newTimelineMatrix: newTl, newMarkerMatrix: newMm } = result;
        exceptionPlanningInfo.title
          ? setExTimelineMatrix(newTl)
          : setTimelineMatrix(newTl);
        exceptionPlanningInfo.title
          ? setExMarkerMatrix(newMm)
          : setMarkerMatrix(newMm);
        setSavingState(0);
      }
    },
    [handleDaySelection, currentInteractionMode, exceptionPlanningInfo.title]
  );

  const handleSave = () => {
    executeSave(
      tasks,
      markerMatrix,
      timelineMatrix,
      exMarkerMatrix,
      exceptionPlanningInfo,
      exTimelineMatrix,
      setSnackMessage,
      setOpenSnack,
      setExceptionPlanningInfo
    );
  };

  const autoToManualSensorTask = (task: any) => {
    const manualTasksList: any = [];
    const tasksWithoutManual = tasks
      .filter((task) => {
        if (task.state === 2) {
          manualTasksList.push(task);
          return false;
        }
        return true;
      })
      .map((t) => {
        t.id_arguments = t.id_arguments?.map((arg: any) => {
          if (arg.Position_in_arg === 2) {
            if (arg.Value.constructor === String) {
              arg.Value = JSON.parse(arg.Value);
            }
            arg.Value = arg.Value.filter((id: string) => id !== task.devui);
          }
          return arg;
        });
        return t;
      });
    const manualTask: any = structuredClone(defaultTask);
    manualTask.state = 2;
    manualTask.id_arguments = [
      {
        Position_in_arg: 1,
        Value: task.mode,
      },
      {
        Position_in_arg: 2,
        Value: [task.devui],
      },
    ];
    const newTasks = [...tasksWithoutManual, ...manualTasksList, manualTask];
    setSavingState(0);
    setTasks(newTasks);
  };

  const manualToAutoSensorTask = (task: any) => {
    const manualTasks: any = [];
    let plannedTasks = tasks.filter((t: any) => {
      if (t.state === 2) {
        let devuiArray;
        if (t.id_arguments![1].Value.constructor === String) {
          devuiArray = JSON.parse(t.id_arguments![1].Value);
        } else {
          devuiArray = t.id_arguments![1].Value;
        }
        if (devuiArray[0] !== '' + task.devui) {
          manualTasks.push(t);
        }
        return false;
      }
      return true;
    });
    plannedTasks = plannedTasks.map((t) => {
      if (t.id_arguments![1].Value.constructor === String) {
        let devuiArray = JSON.parse(t.id_arguments![1].Value);
        devuiArray.push(task.devui);
        t.id_arguments![1].Value = devuiArray;
      } else if (Array.isArray(t.id_arguments![1].Value)) {
        t.id_arguments![1].Value.push(task.devui);
      }
      return t;
    });
    setSavingState(0);
    setTasks([...plannedTasks, ...manualTasks]);
  };

  const handleSelect = (event: any) => {
    const { name, value } = event.target;
    const updatedTask = tasks.map((t: any) => {
      if (t.state === 2) {
        let devuiArray = t.id_arguments![1].Value;
        if (t.id_arguments![1].Value.constructor === String) {
          devuiArray = JSON.parse(t.id_arguments![1].Value);
        }
        if (devuiArray.includes(name)) {
          t.id_arguments[0].Value = value;
        }
      }
      return t;
    });
    setSavingState(0);
    setTasks(updatedTask);
  };

  const handleClose = () => {
    setOpenSnack(false);
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={openSnack}
        onClose={handleClose}
        message={<span>{snackMessage}</span>}
        key={snackMessage}
        autoHideDuration={3000}
      />
    );
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  return (
    <div className={S.container}>
      <PlanningVueLoader isOpen={isBusy} savingState={savingState} />
      {renderSnackbar()}
      <div className={S.mainLayout}>
        <div className={`${S.rightColumn}`}>
          <PlanningSensorList
            modes={defaultModes}
            tasks={tasks}
            projectId={
              props.projectId ? props.projectId : actualView?.project_id
            }
            viewType={actualView?.view_type}
            autoToManual={autoToManualSensorTask}
            manualToAuto={manualToAutoSensorTask}
            handleSelect={handleSelect}
          />
        </div>
        <div className={S.flexGrowContainer}>
          <h3 className={S.title}>{defaultTask.name}</h3>
          <WeekHeader />

          <div className={[S.dayBox, S.modesBox, S.leftColumn].join(' ')}>
            <Box>
              <Tabs
                value={tabValue}
                onChange={handleTabNav}
                aria-label="planning tabs"
                centered
              >
                <Tab label="Planning hebdomadaire" {...a11yProps(0)} />
                <Tab label="Planning d'exceptions" {...a11yProps(1)} />
              </Tabs>
            </Box>
            {tabValue !== 1 && tabValue !== 2 && (
              <PlanningVueControls
                defaultModes={defaultModes}
                selectedMode={selectedMode}
                currentInteractionMode={currentInteractionMode}
                setSelectedMode={setSelectedMode}
                setCurrentInteractionMode={setCurrentInteractionMode}
                toggleMarkerMode={toggleMarkerMode}
                toggleRemoveMode={toggleRemoveMode}
                toggleCopyMode={toggleCopyMode}
              />
            )}
            <PlanningVueTabs
              tabValue={tabValue}
              onTabChange={handleTabNav}
              defaultModes={defaultModes}
              selectedMode={selectedMode}
              currentInteractionMode={currentInteractionMode}
              setCurrentInteractionMode={setCurrentInteractionMode}
              timelineMatrix={timelineMatrix}
              markerMatrix={markerMatrix}
              exTimelineMatrix={exTimelineMatrix}
              exMarkerMatrix={exMarkerMatrix}
              handleTimelineMatrix={handleTimelineMatrix}
              handleMarkerMatrix={handleMarkerMatrix}
              onDayClick={onDaySelected}
              tasks={tasks}
              exceptionPlanningInfo={exceptionPlanningInfo}
              setExPlanningInfo={setExPlanningInfo}
              unavailableDatesPeriodes={getUnavailableDatesPeriodes(tasks)}
              displayExPlanning={displayExPlanning}
              removeExceptionPlanning={removeExceptionPlanning}
              sourceDayForCopy={sourceDayForCopy}
              targetDaysForCopy={targetDaysForCopy}
              a11yProps={a11yProps}
            />
          </div>
        </div>
      </div>
      <PlanningFooter handleClick={handleSave} savingState={savingState} />
    </div>
  );
};

export default Planning;
